/**
 * Wiki data hooks (SOUND-316).
 * Read-only — content is seeded via sound-api/scripts/seed-wiki.mjs.
 */
import { useState, useEffect, useCallback } from "react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";

export function useWikiCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.listWikiCategories,
          { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
          companyId
        );
        if (!cancelled) setCategories(res?.data?.data || []);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, auth?.user?.id, auth?.user?.accessToken]);

  return { categories, loading, error };
}

export function useWikiArticles({ category = "", search = "" } = {}) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  const fetchArticles = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (category) params.category = category;
      if (search) params.search = search;

      const res = await makeApiRequest(
        "get",
        apiRoutes.listWikiArticles(params),
        { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
        companyId
      );
      setArticles(res?.data?.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [companyId, auth?.user?.id, auth?.user?.accessToken, category, search]);

  useEffect(() => {
    fetchArticles();
  }, [fetchArticles]);

  return { articles, loading, error, refetch: fetchArticles };
}

export function useWikiArticle(slug) {
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const companyId = useEffectiveCompanyId();

  useEffect(() => {
    if (!companyId || !slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getWikiArticle(slug),
          { id: auth?.user?.id, accessToken: auth?.user?.accessToken },
          companyId
        );
        if (!cancelled) setArticle(res?.data?.data || null);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, auth?.user?.id, auth?.user?.accessToken, slug]);

  return { article, loading, error };
}
